import * as v from 'valibot';

import {
  ActionButtonSchema,
  AirportSchema,
  type AssertFlightTemplate,
  FlightSegmentSchema,
  PriceSchema,
} from './flights-common';

export const FareBenefitSchema = v.object({
  // e.g., "Benefits worth ₹3,150 included -"
  title: v.string(),

  // e.g., ["Free date change", "Free Cancellation"]
  details: v.array(v.string()),

  // e.g., "info icon"
  icon: v.optional(v.string()),
});

export type FareBenefit = v.InferOutput<typeof FareBenefitSchema>;

export const AmenitySchema = v.object({
  // e.g., "Cabin / check-in"
  label: v.string(),

  // e.g., "7Kgs / 2x15Kgs"
  value: v.string(),

  // e.g., true, false (if included, show tick icon, otherwise show minus icon)
  included: v.boolean(),
});

export type Amenity = v.InferOutput<typeof AmenitySchema>;

export const FareOptionSchema = v.object({
  // e.g., "fare1", "fare2", "fare3"
  id: v.string(),

  // e.g., "MMT Special Fare", "Eco Value", "Eco Classic"
  fareType: v.string(),

  // e.g., true, false (if true, show MMT Special Fare badge)
  mmtSpecialFare: v.boolean(),

  price: v.optional(PriceSchema),
  benefits: v.optional(FareBenefitSchema),
  amenities: v.array(AmenitySchema),

  // e.g., "View Seat, meal and delight"
  viewDetailsLabel: v.optional(v.string()),

  // e.g., true, false (if true, the fare is selected)
  selected: v.boolean(),

  // e.g., [{ label: 'Lock Price', type: 'outline' }, { label: 'Selected', type: 'solid' }]
  actions: v.array(ActionButtonSchema),
});

export type FareOption = v.InferOutput<typeof FareOptionSchema>;

export const StepStatusSchema = v.optional(
  v.union([v.literal('completed'), v.literal('active'), v.literal('pending')]),
);

export const RouteSchema = v.object({
  // e.g., { code: "BLR", city: "Bangalore", country: "India", displayName: "Bangalore, India" }
  from: AirportSchema,

  // e.g., { code: "BLR", city: "Bangalore", country: "India", displayName: "Bangalore, India" }
  to: AirportSchema,
});

export const SubStepSchema = v.object({
  // e.g., "SELECT FARE", "SELECT SEAT, MEAL & ADD-ONS"
  label: v.string(),

  // e.g., "Select fare", "Select seat" (optional subtitle displayed below the main label)
  subtitle: v.optional(v.string()),

  // e.g., { code: "BLR", city: "Bangalore", country: "India", displayName: "Bangalore, India" }
  route: RouteSchema,
  status: StepStatusSchema,
});

export const SectorIdSchema = v.optional(v.string()); // e.g., "DEL-GOX", "GOI-DEL" to be passed to addons api like seat, meal, baggage, addons, etc.

export type SectorId = v.InferOutput<typeof SectorIdSchema>;

export enum StepType {
  TRAVELLERS = 'travellers',
  ANCILLARY = 'ancillary',
  COUPONS = 'coupons',
  PAYMENT = 'payment',
}

/**
 * Ancillary tab type for the ancillary step
 */
export const AncillaryTabTypeSchema = v.picklist(['seats', 'meals', 'baggage', 'addons']);
export type AncillaryTabType = v.InferOutput<typeof AncillaryTabTypeSchema>;

/**
 * Ancillary tab config from backend — controls which tabs are shown per journey.
 * e.g., { id: "seats", label: "Seats" }
 */
export const AncillaryTabConfigSchema = v.object({
  id: AncillaryTabTypeSchema,
  label: v.string(),
});

export type AncillaryTabConfig = v.InferOutput<typeof AncillaryTabConfigSchema>;

/** Identifies which kind of step this entry represents */
export const StepIdSchema = v.optional(
  v.picklist(['traveller', 'ancillary', 'coupons', 'payment']),
);

export type StepId = v.InferOutput<typeof StepIdSchema>;

export const StepperSchema = v.object({
  // e.g., "traveller" | "ancillary" | "coupons" | "payment"
  stepId: StepIdSchema,

  // e.g., "SELECT FARE", "SELECT TRAVELLERS", "PAYMENT", "COUPONS AND OFFERS"
  label: v.string(),

  // e.g., "Select fare", "Select seat" (optional subtitle displayed below the main label)
  subtitle: v.optional(v.string()),

  // e.g., { code: "BLR", city: "Bangalore", country: "India", displayName: "Bangalore, India" }
  // Note: PAYMENT, SELECT TRAVELLERS, COUPONS AND OFFERS steps don't have a route
  route: v.optional(RouteSchema),
  status: StepStatusSchema,
  subSteps: v.optional(v.array(SubStepSchema)),
  sectorId: SectorIdSchema,

  // For ancillary steps: which journey this step belongs to (0 = first journey, 1 = second, etc.)
  journeyIndex: v.optional(v.number()),

  // For ancillary steps: which tabs to show (e.g., [seats, meals] or [baggage, addons])
  ancillaryTabs: v.optional(v.array(AncillaryTabConfigSchema)),
});

export type StepperData = v.InferOutput<typeof StepperSchema>;

export const ReviewPriceSchema = v.object({
  // e.g., "Review Price"
  title: v.optional(v.string()),

  // e.g., "Total Price / Person"
  subtitle: v.string(),

  // e.g., { amount: 31876, currency: "INR", amountFormatted: "₹ 31, 876", perPersonLabel: "/ Person", displayText: "₹ 31, 876 / Person" }
  price: PriceSchema,

  // e.g., "Fare Breakup"
  ctaText: v.optional(v.string()),
});

export type ReviewPrice = v.InferOutput<typeof ReviewPriceSchema>;

export const PassengerFareBreakupSchema = v.object({
  // e.g., "ADULT", "CHILD", "INFANT"
  passengerType: v.string(),

  // e.g., "Adult" (display name)
  passengerTypeLabel: v.string(),

  // Base fare
  baseFare: PriceSchema,

  // Taxes and fees
  taxes: PriceSchema,

  // Convenience fee
  convenienceFee: PriceSchema,

  // Total fare for this passenger type
  totalFare: PriceSchema,
});

export type PassengerFareBreakup = v.InferOutput<typeof PassengerFareBreakupSchema>;

export const OtherServicesSchema = v.object({
  title: v.string(), // e.g., "Other Services"
  amountFormatted: v.string(), // e.g., "₹ 400"
  amount: v.number(), // e.g., 400
  fareEntities: v.array(
    v.object({
      text: v.string(), // e.g., "Meals", "Seats"
      amountFormatted: v.string(), // e.g., "₹ 400"
    }),
  ),
});

export type OtherServices = v.InferOutput<typeof OtherServicesSchema>;

export const FareBreakupSchema = v.object({
  // e.g., "Fare Breakup", "Price Details"
  title: v.string(),

  // Passenger-wise fare breakdown
  passengerFares: v.pipe(v.array(PassengerFareBreakupSchema), v.minLength(1)),

  // Grand total (sum of all passenger fares)
  grandTotal: PriceSchema,

  // Other services
  otherServices: v.optional(OtherServicesSchema),
});

export type FareBreakup = v.InferOutput<typeof FareBreakupSchema>;

export const FlightSummarySchema = v.object({
  // e.g., array of 1 or 2 segments (onward + return)
  segments: v.pipe(v.array(FlightSegmentSchema), v.minLength(1)),

  // Action buttons at the bottom (Pay Now, Add to Cart, etc.)
  actions: v.array(ActionButtonSchema),
});

export type FlightSummary = v.InferOutput<typeof FlightSummarySchema>;

export const JourneyFareOptionsSchema = v.object({
  // Journey index (0 for outbound, 1 for return)
  journeyIndex: v.number(),
  // Route info for display (e.g., "DEL → GOX")
  route: v.optional(RouteSchema),
  // Fare options for this journey
  fareOptions: v.pipe(v.array(FareOptionSchema), v.minLength(1)),
});

export type JourneyFareOptions = v.InferOutput<typeof JourneyFareOptionsSchema>;

// ============================================================================
// Navigation CTA Types (Backend-Driven Navigation)
// ============================================================================

/**
 * Booking phase - controls the high-level view
 */
export enum BookingPhase {
  ONWARD_FARE = 'onward-fare',
  RETURN_FARE = 'return-fare',
  STEPPER = 'stepper',
}

/**
 * Ancillary tab within the ancillary step
 */
export enum AncillaryTab {
  SEATS = 'seats',
  MEALS = 'meals',
  BAGGAGE = 'baggage',
  ADDONS = 'addons',
}

export const AncillaryTabSchema = v.picklist(Object.values(AncillaryTab));

// ============================================================================
// Stepper Context (CV-Driven Navigation)
// ============================================================================

/**
 * Main step type for the booking stepper
 * Controls which main phase of the booking flow is active
 */
export const MainStepTypeSchema = v.picklist(['traveller', 'ancillary', 'coupons', 'payment']);
export type MainStepType = v.InferOutput<typeof MainStepTypeSchema>;

/**
 * Stepper Context - Chat-driven state from backend
 *
 * This allows the AI agent to control the stepper navigation based on user intent.
 * When the user says something like "select seats for BOM to MAA", the backend
 * sends the appropriate stepperContext to navigate to the correct step.
 *
 * @example
 * // User says "apply this coupon"
 * { mainStep: 'coupons' }
 *
 * @example
 * // User says "select seat for BOM to MAA"
 * { mainStep: 'ancillary', journeyIndex: 0, ancillaryTab: 'seats', sectorId: 'BOM-MAA' }
 *
 * @example
 * // User says "add meals for return flight"
 * { mainStep: 'ancillary', journeyIndex: 1, ancillaryTab: 'meals' }
 */
export const StepperContextSchema = v.object({
  /**
   * Main step to navigate to
   * Values: 'traveller' | 'ancillary' | 'coupons' | 'payment'
   */
  mainStep: MainStepTypeSchema,

  /**
   * For ancillary step: which journey/sector to show (0-based index)
   * - 0 = first journey (onward)
   * - 1 = second journey (return for round-trip, or 2nd leg for multi-city)
   *
   * @example
   * - User says "select seat for BOM to MAA" → journeyIndex: 0 (if BOM-MAA is first journey)
   * - User says "select seat for return" → journeyIndex: 1
   */
  journeyIndex: v.optional(v.number()),

  /**
   * For ancillary step: which tab to activate
   * Values: 'seats' | 'meals' | 'baggage' | 'addons'
   *
   * @example
   * - User says "select seats" → ancillaryTab: 'seats'
   * - User says "add meals" → ancillaryTab: 'meals'
   */
  ancillaryTab: v.optional(AncillaryTabTypeSchema),

  /**
   * For seat selection: which leg within the journey (for connecting flights)
   * Index into the legs array (0 = first leg, 1 = second leg)
   *
   * @example For BOM → DEL → MAA (connecting flight):
   * - User says "select seat for BOM to DEL" → legIndex: 0
   * - User says "select seat for DEL to MAA" → legIndex: 1
   */
  legIndex: v.optional(v.number()),

  /**
   * Sector ID for API calls (seats, meals, baggage, addons)
   * Format: "ORIGIN-DESTINATION" (e.g., "BOM-MAA", "DEL-GOI")
   *
   * This is used to call the correct addon API
   */
  sectorId: v.optional(v.string()),
});

export type StepperContext = v.InferOutput<typeof StepperContextSchema>;

/**
 * Navigation target - defines where to navigate on CTA click
 */
export const NavigationActionPayloadSchema = v.object({
  // High-level phase
  phase: v.optional(
    v.picklist([BookingPhase.ONWARD_FARE, BookingPhase.RETURN_FARE, BookingPhase.STEPPER]),
  ),

  // Step type (only applicable when phase is 'stepper')
  stepType: v.optional(v.picklist(Object.values(StepType))),

  // Ancillary tab (only applicable when stepType is 'ancillary')
  ancillaryTab: v.optional(v.picklist(Object.values(AncillaryTab))),

  // Layover index of current sector (0-based, for multi-layover flows, only applicable when stepType is 'ancillary')
  layoverIndex: v.optional(v.number()),

  // Sector ID for API calls, only applicable when stepType is 'ancillary'
  sectorId: v.optional(SectorIdSchema),
});
export type NavigationActionPayload = v.InferOutput<typeof NavigationActionPayloadSchema>;

// /**
//  * API call to make before/after navigation
//  */
// export const NavigationApiCallSchema = v.object({
//   // Endpoint identifier (e.g., 'prebook', 'validateTraveller', 'selectAddon')
//   endpoint: v.string(),

//   // HTTP method
//   method: v.optional(v.picklist(['GET', 'POST', 'PUT', 'DELETE'])),

//   // Additional params to include in the API call
//   params: v.optional(v.record(v.string(), v.unknown())),

//   // When to make the API call
//   timing: v.optional(v.picklist(['before', 'after'])),
// });
// export type NavigationApiCall = v.InferOutput<typeof NavigationApiCallSchema>;

// /**
//  * Validation rule to check before navigation
//  */
// export const NavigationValidationSchema = v.object({
//   // Type of validation
//   type: v.picklist([
//     'form', // Validate form fields
//     'selection', // Check if required selections are made
//     'api', // Call validation API
//   ]),

//   // Identifier for what to validate (e.g., 'travellerForm', 'seatSelection')
//   target: v.string(),

//   // Error message to show if validation fails
//   errorMessage: v.optional(v.string()),
// });
// export type NavigationValidation = v.InferOutput<typeof NavigationValidationSchema>;

/**
 * Complete Navigation CTA - backend-driven navigation control
 *
 * This schema allows the backend to fully control:
 * 1. Button appearance (label, variant, disabled state)
 * 2. Action payload (what to do on click)
 *
 */

export enum NavigationCtaActionType {
  NEXT = 'next',
  PREVIOUS = 'previous',
  SKIP_TO_PAYMENT = 'skipToPayment',
  PROCEED_TO_PAYMENT = 'proceedToPayment',
}

export const NavigationCtaSchema = v.object({
  // Button display properties
  label: v.string(), // e.g., "Next", "Continue", "Proceed to Payment"
  variant: v.optional(v.picklist(['primary', 'secondary', 'ghost', 'gradient'])),
  disabled: v.optional(v.boolean()),
  leadingIcon: v.optional(v.string()),
  trailingIcon: v.optional(v.string()),
  actionType: v.optional(v.picklist(Object.values(NavigationCtaActionType))), // e.g., "next", "previous", "skipToPayment", "continue", "proceedToPayment", etc.
  actionPayload: v.optional(NavigationActionPayloadSchema),
});

export type NavigationCta = v.InferOutput<typeof NavigationCtaSchema>;

export const FlightsReviewTemplateDataSchema = v.object({
  // Legacy: single array of fare options (for one-way)
  fareOptions: v.optional(v.pipe(v.array(FareOptionSchema), v.minLength(1))),
  // New: journey-wise fare options (for round-trip/multi-city)
  journeyFareOptions: v.optional(v.pipe(v.array(JourneyFareOptionsSchema), v.minLength(1))),
  stepper: v.optional(v.array(StepperSchema)),
  reviewPrice: v.optional(ReviewPriceSchema),
  flightSummary: v.optional(FlightSummarySchema),
  fareBreakup: v.optional(FareBreakupSchema),
  itineraryId: v.string(),
  contextHashKey: v.optional(v.string()),
  // ctaList: v.optional(v.array(NavigationCtaSchema)), // TODO: Replace with ActionButtonSchema
});

export type FlightsReviewTemplateData = v.InferOutput<typeof FlightsReviewTemplateDataSchema>;

export const FlightsReviewTemplateSchema = v.object({
  type: v.literal('flights-review'),
  data: FlightsReviewTemplateDataSchema,
});

export type FlightsReviewTemplate = AssertFlightTemplate<
  v.InferOutput<typeof FlightsReviewTemplateSchema>
>;
